import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaGithub,
  FaExternalLinkAlt,
  FaCode,
  FaRocket,
  FaLightbulb,
} from "react-icons/fa";
import "./styles/projectModal.css";
import PokedexLogo from "./PokedexLogo";

const ProjectModal = ({ project, isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState("description");
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [zoomedLogo, setZoomedLogo] = useState(null);
  
    // ✅ Hooks toujours appelés (même si project est null)
    useEffect(() => {
      const onEsc = (e) => {
        if (e.key === "Escape") {
          if (zoomedLogo) setZoomedLogo(null);
          else if (isOpen) onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", onEsc);
        document.body.style.overflow = "hidden";
        document.body.classList.add("modal-open");
      }
      return () => {
        document.removeEventListener("keydown", onEsc);
        document.body.style.overflow = "";
        document.body.classList.remove("modal-open");
      };
    }, [isOpen, onClose, zoomedLogo]);
  
    // ⬇️ Fais le guard APRÈS les hooks
    if (!project) return null;

  // --- Variants Framer ---
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.18 } },
    exit: { opacity: 0, transition: { duration: 0.18 } },
  };

  // Pas de translateY ici : le centrage est géré par .modal-frame (CSS)
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.25, ease: "easeOut" } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.18 } },
  };

  // --- Helpers ---
  const images = project.images && project.images.length > 0
    ? project.images
    : project.image
      ? [project.image]
      : [];

  const hasGallery = images.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="modal-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          {/* Cadre fixe centré (évite les conflits transform avec Framer) */}
          <div className="modal-frame" onClick={onClose}>
            {/* Modal animé */}
            <motion.div
              className="project-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-modal-title"
            >
              {/* Header */}
              <div className="modal-header">
                <div className="modal-title-section">
                  <h2 id="project-modal-title" className="modal-title">
                    {project.title}
                  </h2>

                  {(project.icons?.length || project.customLogo) && (
                    <div className="modal-icons">
                      {project.customLogo === "pokedex" && (
                        <div className="modal-icon-wrapper">
                          <PokedexLogo size="small" />
                        </div>
                      )}

                      {project.icons?.map((icon, i) => {
                        const zoomable =
                          icon?.includes("MovieFrenzy") ||
                          icon?.includes("projectImage04") ||
                          icon?.includes("Angular");
                        return (
                          <img
                            key={i}
                            src={icon}
                            alt={`Logo ${i + 1}`}
                            className={`modal-icon ${zoomable ? "zoomable-logo" : ""}`}
                            onClick={() => zoomable && setZoomedLogo(icon)}
                            title={zoomable ? "Cliquez pour agrandir" : ""}
                          />
                        );
                      })}
                    </div>
                  )}

                  {project.category && (
                    <span className="modal-category">{project.category}</span>
                  )}
                </div>

                <button
                  className="modal-close"
                  onClick={onClose}
                  aria-label="Fermer le modal"
                  title="Fermer"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Galerie */}
              {hasGallery && (
                <div className="modal-gallery">
                  <div className="gallery-main">
                    <img
                      src={images[currentImageIndex]}
                      alt={`${project.title} – ${currentImageIndex + 1}`}
                      className="gallery-image"
                    />

                    {images.length > 1 && (
                      <div className="gallery-nav">
                        <button
                          className="gallery-prev"
                          aria-label="Image précédente"
                          onClick={() =>
                            setCurrentImageIndex((prev) =>
                              prev === 0 ? images.length - 1 : prev - 1
                            )
                          }
                        >
                          ‹
                        </button>
                        <button
                          className="gallery-next"
                          aria-label="Image suivante"
                          onClick={() =>
                            setCurrentImageIndex((prev) => (prev + 1) % images.length)
                          }
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </div>

                  {images.length > 1 && (
                    <div className="gallery-thumbnails">
                      {images.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Vignette ${index + 1}`}
                          className={`thumbnail ${
                            currentImageIndex === index ? "active" : ""
                          }`}
                          onClick={() => setCurrentImageIndex(index)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Onglets */}
              <div className="modal-tabs">
                <button
                  className={`tab-button ${activeTab === "description" ? "active" : ""}`}
                  onClick={() => setActiveTab("description")}
                >
                  Description
                </button>
                <button
                  className={`tab-button ${activeTab === "technologies" ? "active" : ""}`}
                  onClick={() => setActiveTab("technologies")}
                >
                  Technologies
                </button>
                <button
                  className={`tab-button ${activeTab === "details" ? "active" : ""}`}
                  onClick={() => setActiveTab("details")}
                >
                  Détails
                </button>
              </div>

              {/* Contenu des tabs */}
              <div className="modal-content">
                <AnimatePresence mode="wait">
                  {activeTab === "description" && (
                    <motion.div
                      key="description"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="tab-content"
                    >
                      <h3>
                        <FaLightbulb /> À propos du projet
                      </h3>
                      <p>{project.fullDescription || project.description}</p>

                      {project.features?.length > 0 && (
                        <>
                          <h4 className="features-title">
                            <FaRocket className="features-icon" /> Fonctionnalités principales
                          </h4>
                          <ul className="features-list">
                            {project.features.map((feature, idx) => (
                              <li key={idx}>
                                <span className="feature-bullet">✓</span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </motion.div>
                  )}

                  {activeTab === "technologies" && (
                    <motion.div
                      key="technologies"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="tab-content"
                    >
                      <h3>
                        <FaCode /> Stack technique
                      </h3>
                      <div className="tech-grid">
                        {project.technologies?.map((tech, idx) => (
                          <motion.div
                            key={idx}
                            className="tech-item"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            whileHover={{ scale: 1.05, y: -5 }}
                          >
                            <span className="tech-name">{tech}</span>
                          </motion.div>
                        ))}
                      </div>

                      {project.techDetails && (
                        <div className="tech-details">
                          <h4>Architecture technique</h4>
                          <p>{project.techDetails}</p>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {activeTab === "details" && (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="tab-content"
                    >
                      {project.challenges?.length > 0 && (
                        <>
                          <h3>💪 Défis relevés</h3>
                          <ul className="challenges-list">
                            {project.challenges.map((c, idx) => (
                              <li key={idx}>{c}</li>
                            ))}
                          </ul>
                        </>
                      )}

                      {project.role && (
                        <>
                          <h3>👨‍💻 Mon rôle</h3>
                          <p>{project.role}</p>
                        </>
                      )}

                      {project.duration && (
                        <>
                          <h3>📅 Durée du projet</h3>
                          <p>{project.duration}</p>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-link live-link"
                  >
                    <FaExternalLinkAlt /> Voir le site
                  </a>
                )}

                {project.github && (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-link github-link"
                  >
                    <FaGithub /> Code source
                  </a>
                )}

                {!project.link && project.archived && (
                  <span className="archived-badge">
                    📁 Projet archivé {project.archivedDate ? `(${project.archivedDate})` : ""}
                  </span>
                )}
              </div>
            </motion.div>
          </div>

          {/* Zoom logo */}
          {zoomedLogo && (
            <motion.div
              className="logo-zoom-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setZoomedLogo(null)}
            >
              <motion.img
                src={zoomedLogo}
                alt="Logo agrandi"
                className="logo-zoomed"
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.5 }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className="zoom-close"
                onClick={() => setZoomedLogo(null)}
                aria-label="Fermer le zoom"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
